import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Input, Modal, Select, Space, Spin, Table, Tag, TreeSelect } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { NumericFormat } from 'react-number-format';
import { getApi, postApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import { AppDispatch } from 'redux/store';
import { getEmployeeAction } from 'redux/action/employeeAction';
import { customerOptionsAction } from 'redux/action/customerAction';
import { classOptionsAction } from 'redux/action/classAction';
import type {
	AllocationRowState,
	EmployeeGroup,
	WizardAction,
	WizardState,
} from '../wizardReducer';
import {
	buildEmployeeGroups,
	buildImportPayload,
	parseBulkImportErrors,
	validatePercentSums,
	canImport,
	findGroupByEmployeeId,
	groupDisplayName,
	buildTemplateGroup,
	canImportTemplate,
	expandTemplate,
	isTemplateMode,
	selectableEmployees,
	splitEqually,
	templateIssues,
	templateTargetEmployees,
	type PreValidationError,
	type RosterEmployee,
	type TreeOption,
} from '../directAllocationResolve';
import TemplatePreview from './TemplatePreview';
import styles from './Step3Preview.module.scss';

interface Step3PreviewProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	companyId: string;
	payPeriodId: string;
	onBack: () => void;
	onCancel: () => void;
	onImported: () => void;
}

interface FlatRow {
	group: EmployeeGroup;
	row: AllocationRowState;
	isFirstInGroup: boolean;
	groupRowSpan: number;
}

/**
 * Step 3 — Preview. Groups parsed rows by employee, surfaces fuzzy-match status
 * for Employee/Customer/Class, lets the user resolve anything ambiguous, and
 * validates each employee's allocations sum to 100% before Import is enabled.
 */
export default function Step3Preview({
	state,
	dispatch,
	companyId,
	payPeriodId,
	onBack,
	onCancel,
	onImported,
}: Step3PreviewProps) {
	const { data: employeeRoster } = useSelector((s: any) => s.employees) as {
		data: RosterEmployee[];
	};
	const { optionData: customerOptions } = useSelector((s: any) => s.customer) as {
		optionData: TreeOption[];
	};
	const { optionData: classOptions } = useSelector((s: any) => s.class) as {
		optionData: TreeOption[];
	};

	const reduxDispatch = useDispatch<AppDispatch>();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [importErrors, setImportErrors] = useState<PreValidationError[]>([]);
	const [generalError, setGeneralError] = useState<string | null>(null);
	const [rosterError, setRosterError] = useState<string | null>(null);
	const [highlightedGroupId, setHighlightedGroupId] = useState<string | null>(null);
	const tableWrapRef = useRef<HTMLDivElement>(null);
	const templateMode = isTemplateMode(state.columnMappings);
	// Template mode: employees with direct allocations in this pay period (to warn
	// they'll be replaced). null until loaded, or if loading failed.
	const [existingAllocationIds, setExistingAllocationIds] = useState<Set<string> | null>(null);
	const isBuilt = templateMode ? state.templateGroup !== null : state.employeeGroups.length > 0;
	// Nothing upstream guarantees the employee/customer/class lists are already in
	// Redux by the time this wizard opens — EmployeeCostsMain never dispatches the
	// employee roster fetch itself (only the Employee Cost table's own mount does,
	// and only for customer/class, not employees). Building groups off of whatever
	// happened to be in the store at mount time silently matched every employee to
	// "Review" whenever the roster was still empty. Fetch fresh here and build off
	// the resolved payloads directly, so the first build always sees real data.
	const [isResolvingRoster, setIsResolvingRoster] = useState(!isBuilt);

	useEffect(() => {
		if (isBuilt) {
			setIsResolvingRoster(false);
			return;
		}

		let cancelled = false;

		(async () => {
			const [rosterResult, customers, classes] = await Promise.all([
				reduxDispatch(getEmployeeAction())
					.unwrap()
					.then((value) => ({ ok: true as const, value }))
					.catch((err) => ({ ok: false as const, value: employeeRoster ?? [], err })),
				reduxDispatch(customerOptionsAction())
					.unwrap()
					.catch(() => customerOptions ?? []),
				reduxDispatch(classOptionsAction())
					.unwrap()
					.catch(() => classOptions ?? []),
			]);

			if (cancelled) return;

			const roster = rosterResult.value;
			if (!rosterResult.ok) {
				// Swallowing this used to leave the Employee dropdown silently
				// empty with no indication why — surface it instead.
				const message =
					(rosterResult.err as any)?.message ??
					(rosterResult.err as any)?.response?.data?.message;
				setRosterError(
					typeof message === 'string' && message
						? `Could not load the employee list: ${message}`
						: 'Could not load the employee list. The Employee dropdown will be empty until this succeeds — please retry.'
				);
			}

			if (templateMode) {
				dispatch({
					type: 'TEMPLATE_INITIALIZED',
					templateGroup: buildTemplateGroup(
						state.rawRows,
						state.columnMappings,
						customers ?? [],
						classes ?? []
					),
				});
			} else {
				dispatch({
					type: 'GROUPS_INITIALIZED',
					employeeGroups: buildEmployeeGroups(
						state.rawRows,
						state.columnMappings,
						roster ?? [],
						customers ?? [],
						classes ?? []
					),
				});
			}
			setIsResolvingRoster(false);
		})();

		return () => {
			cancelled = true;
		};
		// Runs once on entry — guarded above (and in the reducer) against
		// Back-then-Next re-entry re-building over the user's manual resolutions.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// A failed import's error list describes the groups as they were when Import
	// was clicked. Once the user edits anything it's stale — leaving it up made
	// it disagree with the per-employee totals while Import was enabled (CAP-115).
	// The live warning banner below keeps reporting what's still wrong.
	useEffect(() => {
		setImportErrors([]);
		setGeneralError(null);
	}, [state.employeeGroups, state.templateGroup, state.templateEmployeeIds]);

	useEffect(() => {
		if (!templateMode || !payPeriodId) return;
		let cancelled = false;
		getApi('/employee-config/employees-with-allocation', { companyId, payPeriodId })
			.then((res: { data?: { data?: { employeeIds?: string[] } } }) => {
				if (!cancelled) setExistingAllocationIds(new Set(res?.data?.data?.employeeIds ?? []));
			})
			// Not fatal: the confirmation falls back to a generic "will be replaced" warning
			.catch(() => !cancelled && setExistingAllocationIds(null));
		return () => {
			cancelled = true;
		};
	}, [templateMode, companyId, payPeriodId]);

	const groups = state.employeeGroups;
	const validations = new Map(validatePercentSums(groups).map((v) => [v.groupId, v]));

	const scrollToGroup = (group: EmployeeGroup) => {
		setHighlightedGroupId(group.groupId);
		tableWrapRef.current
			?.querySelector(`[data-row-key="${group.rows[0]?.rowId}"]`)
			?.scrollIntoView({ block: 'center', behavior: 'smooth' });
	};

	const employeeLink = (group: EmployeeGroup, label = groupDisplayName(group)) => (
		<Button type="link" size="small" className={styles['step3__employee-link']} onClick={() => scrollToGroup(group)}>
			{label}
		</Button>
	);
	const reviewGroups = groups.filter((g) => g.matchStatus !== 'matched');
	const invalidTotals = groups.filter((g) => !validations.get(g.groupId)?.isValid);
	const pendingCustomerClass = groups.reduce(
		(count, g) => count + g.rows.filter((r) => r.customerReview || r.classReview).length,
		0
	);
	const ready = canImport(groups);

	// Rows with no % (file had no % column, or a blank cell) — offer an equal
	// split, but never apply one without the user asking.
	const hasMissingPercent = (g: EmployeeGroup) => g.rows.some((r) => r.percent === null);
	const groupsMissingPercent = groups.filter(hasMissingPercent);
	const splitGroupsEqually = (targets: EmployeeGroup[]) =>
		dispatch({
			type: 'GROUP_PERCENTS_SET',
			payload: targets.map((g) => ({ groupId: g.groupId, percents: splitEqually(g.rows.length) })),
		});

	// Template mode
	const template = state.templateGroup;
	const templateValidation = template ? validatePercentSums([template])[0] : null;
	const activeEmployees = selectableEmployees(employeeRoster ?? []);
	const selectedEmployees = templateTargetEmployees(
		state.templateApplyTo,
		activeEmployees,
		state.templateEmployeeIds
	);
	const pendingTemplateIssues = templateIssues(
		template,
		state.templateApplyTo,
		selectedEmployees.length
	);
	const templateReady =
		pendingTemplateIssues.length === 0 &&
		canImportTemplate(template, selectedEmployees.map((e) => e.id));

	const flatRows: FlatRow[] = groups.flatMap((group) =>
		group.rows.map((row, idx) => ({
			group,
			row,
			isFirstInGroup: idx === 0,
			groupRowSpan: group.rows.length,
		}))
	);

	const customerTreeData = (customerOptions ?? []).map((item: any) =>
		item.title === 'Select Customer' ? { ...item, title: 'Blank Customer', value: '' } : item
	);
	const classTreeData = (classOptions ?? []).map((item: any) =>
		item.title === 'Select Class' ? { ...item, title: 'Blank Class', value: '' } : item
	);

	const employeeRosterOptions = (employeeRoster ?? []).map((e) => ({
		value: e.id,
		label: e.fullName,
	}));

	// Employee mode shows the note under its own dropdown (rows are merged per
	// employee, so there's no room for a separate line). Template mode puts one
	// combined note on its own line under the row — see rowNote.
	const unmatchedNote = (text: string, kind: 'Customer' | 'Class') =>
		templateMode ? null : (
			<div className={styles['step3__unmatched']}>
				&quot;{text}&quot; isn&apos;t in QuickBooks — using Blank {kind}
			</div>
		);

	const rowNote = (row: AllocationRowState) => {
		const parts = [
			row.customerUnmatched && `Customer "${row.customerText}"`,
			row.classUnmatched && `Class "${row.classText}"`,
		].filter(Boolean);
		if (parts.length === 0) return null;
		const blanks = [row.customerUnmatched && 'Blank Customer', row.classUnmatched && 'Blank Class']
			.filter(Boolean)
			.join(' and ');
		return (
			<div className={styles['step3__row-note']}>
				⚠ {parts.join(' and ')} {parts.length > 1 ? 'aren’t' : 'isn’t'} in QuickBooks — using{' '}
				{blanks}. Pick one above to change it.
			</div>
		);
	};

	const columns = [
		{
			title: 'Employee',
			key: 'employee',
			onCell: (record: FlatRow) => ({
				rowSpan: record.isFirstInGroup ? record.groupRowSpan : 0,
			}),
			render: (_: unknown, record: FlatRow) => {
				const { group } = record;
				const validation = validations.get(group.groupId);
				return (
					<div className={styles['step3__employee-cell']}>
						{group.matchStatus === 'matched' ? (
							<>
								<span className={styles['step3__employee-name']}>
									{group.matchedEmployeeName ?? group.csvName}
								</span>
								<Tag color="green">Matched</Tag>
							</>
						) : (
							<>
								<span className={styles['step3__employee-name']}>{group.csvName}</span>
								<Tag color="orange">Review</Tag>
								<Select
									className={styles['step3__select']}
									showSearch
									placeholder="Select employee"
									options={employeeRosterOptions}
									filterOption={(input, option) =>
										(option?.label ?? '')
											.toString()
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									onChange={(employeeId: string, option: any) =>
										dispatch({
											type: 'GROUP_EMPLOYEE_OVERRIDDEN',
											payload: {
												groupId: group.groupId,
												employeeId,
												employeeName: option?.label ?? null,
											},
										})
									}
								/>
							</>
						)}
						{validation && (
							<span
								className={`${styles['step3__employee-total']} ${
									validation.isValid
										? styles['step3__employee-total--valid']
										: styles['step3__employee-total--invalid']
								}`}
							>
								Total: {validation.total}%
								{validation.isRoundedTo100 && ' (rounding — saved as 100%)'}
							</span>
						)}
						{hasMissingPercent(group) && (
							<Button
								type="link"
								size="small"
								className={styles['step3__employee-link']}
								onClick={() => splitGroupsEqually([group])}
							>
								Split equally
							</Button>
						)}
					</div>
				);
			},
		},
		{
			title: 'Customer',
			key: 'customer',
			render: (_: unknown, record: FlatRow) => {
				const { group, row } = record;
				const displayValue = row.customerName === '-' ? 'Blank Customer' : row.customerName;
				return (
					<>
					<TreeSelect
						className={`${styles['step3__tree-select']} ${
							row.customerReview ? styles['step3__tree-select--review'] : ''
						}`}
						dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
						size="middle"
						placeholder={row.customerText || 'Customer'}
						value={displayValue || undefined}
						showSearch
						treeData={customerTreeData}
						treeDefaultExpandAll
						filterTreeNode={(inputValue, treeNode) =>
							String(treeNode.props.title ?? '')
								.toLowerCase()
								.includes(inputValue.toLowerCase())
						}
						onChange={(value: any, data: any) =>
							dispatch({
								type: 'ROW_CUSTOMER_CHANGED',
								payload: {
									groupId: group.groupId,
									rowId: row.rowId,
									customerId: value,
									customerName: data?.[0],
								},
							})
						}
					/>
					{row.customerUnmatched && unmatchedNote(row.customerText, 'Customer')}
					</>
				);
			},
		},
		{
			title: 'Class',
			key: 'class',
			render: (_: unknown, record: FlatRow) => {
				const { group, row } = record;
				const displayValue = row.className === '-' ? 'Blank Class' : row.className;
				return (
					<>
					<TreeSelect
						className={`${styles['step3__tree-select']} ${
							row.classReview ? styles['step3__tree-select--review'] : ''
						}`}
						dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
						size="middle"
						placeholder={row.classText || 'Class'}
						value={displayValue || undefined}
						showSearch
						treeData={classTreeData}
						treeDefaultExpandAll
						filterTreeNode={(inputValue, treeNode) =>
							String(treeNode.props.title ?? '')
								.toLowerCase()
								.includes(inputValue.toLowerCase())
						}
						onChange={(value: any, data: any) =>
							dispatch({
								type: 'ROW_CLASS_CHANGED',
								payload: {
									groupId: group.groupId,
									rowId: row.rowId,
									classId: value,
									className: data?.[0],
								},
							})
						}
					/>
					{row.classUnmatched && unmatchedNote(row.classText, 'Class')}
					</>
				);
			},
		},
		{
			title: '% of Hours',
			key: 'percent',
			render: (_: unknown, record: FlatRow) => {
				const { group, row } = record;
				return (
					<NumericFormat
						className={styles['step3__percent-input']}
						value={row.percent ?? ''}
						decimalScale={2}
						suffix="%"
						customInput={Input}
						onValueChange={(values) =>
							dispatch({
								type: 'ROW_PERCENT_CHANGED',
								payload: {
									groupId: group.groupId,
									rowId: row.rowId,
									percent: values.floatValue ?? null,
								},
							})
						}
						isAllowed={(values) => {
							const { floatValue } = values;
							return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
						}}
					/>
				);
			},
		},
	];

	const handleImport = async (groupsToImport: EmployeeGroup[]) => {
		setImportErrors([]);
		setGeneralError(null);
		setIsSubmitting(true);
		try {
			const payload = buildImportPayload(groupsToImport, { companyId, payPeriodId });
			const response = await postApi('/employee-config/bulk-import', payload);
			// The import is all-or-nothing, so on success every employee was saved
			const employeesUpdated = response?.data?.data?.employeesUpdated ?? payload.rows.length;
			toastText(`Imported direct allocations for ${employeesUpdated} employee(s).`, 'success');
			onImported();
		} catch (err) {
			const errors = parseBulkImportErrors(err);
			if (errors.length > 0) {
				setImportErrors(errors);
			} else {
				const message =
					(err as any)?.response?.data?.message ??
					'Failed to import employee direct allocations. Please try again.';
				setGeneralError(typeof message === 'string' ? message : 'Failed to import.');
				toastText('Failed to import employee direct allocations', 'error');
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	// Applying one allocation to many employees replaces whatever they have now
	// and moves them off time-activity allocation, so spell that out first.
	const confirmTemplateImport = () => {
		if (!template) return;
		const replaced = existingAllocationIds
			? selectedEmployees.filter((e) => existingAllocationIds.has(e.id)).length
			: null;
		Modal.confirm({
			title: `Apply this allocation to ${selectedEmployees.length} employee(s)?`,
			width: 520,
			content: (
				<ul className={styles['step3__errors-list']}>
					<li>
						{replaced === null
							? 'Any direct allocations these employees already have for this pay period will be replaced.'
							: replaced > 0
								? `${replaced} of them already have direct allocations for this pay period, which will be replaced.`
								: 'None of them have direct allocations for this pay period yet.'}
					</li>
					<li>
						Their hours for this pay period will be allocated using this split instead of their
						time activities.
					</li>
				</ul>
			),
			centered: true,
			okText: 'Apply',
			cancelText: 'Cancel',
			// Not the global .btn-black: its block layout pushed Apply onto its own line
			okButtonProps: { className: styles['step3__confirm-ok'] },
			onOk: () => handleImport(expandTemplate(template, selectedEmployees)),
		});
	};

	if (isResolvingRoster) {
		return (
			<div className={styles['step3']}>
				<div className={styles['step3__loading']}>
					<Spin size="large" />
					<p>Matching employees, customers, and classes…</p>
				</div>
			</div>
		);
	}

	const errorAlerts = (
		<>
			{importErrors.length > 0 && (
				<Alert
					className={styles['step3__errors']}
					type="error"
					showIcon
					message="Could not import"
					description={
						<ul className={styles['step3__errors-list']}>
							{importErrors.map((e, i) => {
								const group = findGroupByEmployeeId(groups, e.employeeId);
								return (
									<li key={i}>
										{e.message}
										{group && <> {employeeLink(group, 'Show')}</>}
									</li>
								);
							})}
						</ul>
					}
				/>
			)}

			{generalError && (
				<Alert className={styles['step3__errors']} type="error" showIcon message={generalError} />
			)}
		</>
	);

	const footer = (
		<div className={`${styles['step3__footer']} ${templateMode ? styles['step3__footer--status'] : ''}`}>
			{templateMode && (
				<div className={styles['step3__footer-status']}>
					{pendingTemplateIssues.length > 0 ? (
						<>
							<strong>Before importing:</strong> {pendingTemplateIssues.join(' ')}
						</>
					) : (
						<span className={styles['step3__employee-total--valid']}>
							Ready to apply to {selectedEmployees.length} employee(s).
						</span>
					)}
				</div>
			)}
			<Space>
				<Button className={styles['step3__btn-secondary']} onClick={onCancel}>
					Cancel
				</Button>
				<Button className={styles['step3__btn-secondary']} onClick={onBack}>
					Previous
				</Button>
				<Button
					type="primary"
					disabled={(templateMode ? !templateReady : !ready) || isSubmitting}
					loading={isSubmitting}
					onClick={() => (templateMode ? confirmTemplateImport() : void handleImport(groups))}
					className="btn-black"
					style={{ height: '41px' }}
				>
					Import
				</Button>
			</Space>
		</div>
	);

	if (templateMode && template) {
		const templateRows: FlatRow[] = template.rows.map((row, idx) => ({
			group: template,
			row,
			isFirstInGroup: idx === 0,
			groupRowSpan: template.rows.length,
		}));
		return (
			<div className={`${styles['step3']} ${styles['step3--template']}`}>
				{rosterError && (
					<Alert className={styles['step3__errors']} type="error" showIcon message={rosterError} />
				)}

				{errorAlerts}

				<TemplatePreview<FlatRow>
					template={template}
					validation={templateValidation}
					lineRows={templateRows}
					lineColumns={columns.slice(1) as ColumnsType<FlatRow>}
					lineRowKey={(r) => r.row.rowId}
					lineNote={(r) => rowNote(r.row)}
					applyTo={state.templateApplyTo}
					activeEmployees={activeEmployees}
					selectedIds={state.templateEmployeeIds}
					targetEmployees={selectedEmployees}
					existingAllocationIds={existingAllocationIds}
					dispatch={dispatch}
					onSplitEqually={() => splitGroupsEqually([template])}
				/>

				{footer}
			</div>
		);
	}

	return (
		<div className={styles['step3']}>
			<Alert
				className={styles['step3__banner']}
				type="info"
				showIcon
				message={`${groups.length} employee(s), ${flatRows.length} allocation row(s).`}
			/>

			{rosterError && (
				<Alert className={styles['step3__errors']} type="error" showIcon message={rosterError} />
			)}

			{(reviewGroups.length > 0 || invalidTotals.length > 0 || pendingCustomerClass > 0) && (
				<Alert
					className={styles['step3__banner']}
					type="warning"
					showIcon
					message="Resolve the following before importing"
					description={
						<ul className={styles['step3__errors-list']}>
							{reviewGroups.length > 0 && (
								<li>{reviewGroups.length} employee(s) need to be matched to an existing employee.</li>
							)}
							{groupsMissingPercent.length > 0 && (
								<li>
									{groupsMissingPercent.length} employee(s) have lines with no % of Hours.{' '}
									<Button
										type="link"
										size="small"
										className={styles['step3__employee-link']}
										onClick={() => splitGroupsEqually(groupsMissingPercent)}
									>
										Split all of them equally
									</Button>
								</li>
							)}
							{invalidTotals.length > 0 && (
								<li>
									{invalidTotals.length} employee(s) have allocations that don&apos;t total 100%:{' '}
									{invalidTotals.map((g, i) => (
										<span key={g.groupId}>
											{i > 0 && ', '}
											{employeeLink(g, `${groupDisplayName(g)} (${validations.get(g.groupId)?.total}%)`)}
										</span>
									))}
								</li>
							)}
							{pendingCustomerClass > 0 && (
								<li>
									{pendingCustomerClass} row(s) need a Customer/Class picked — the QuickBooks lists
									couldn&apos;t be loaded to check them.
								</li>
							)}
						</ul>
					}
				/>
			)}

			{errorAlerts}

			<div ref={tableWrapRef}>
				<Table
					className={styles['step3__table']}
					dataSource={flatRows}
					columns={columns}
					rowKey={(r: FlatRow) => r.row.rowId}
					rowClassName={(r: FlatRow) =>
						r.group.groupId === highlightedGroupId ? styles['step3__row--highlighted'] : ''
					}
					pagination={false}
					scroll={{ y: 400 }}
					size="small"
				/>
			</div>

			{footer}
		</div>
	);
}
